import ApiService from './api';
import { 
  PaymentIntent, 
  Payment, 
  Refund, 
  Dispute,
  Order,
  OrderStatus,
  PaymentStatus
} from '@/types';

export interface CreatePaymentIntentRequest {
  amount: number;
  currency: string;
  description?: string;
  payment_method_types: string[];
  customer_id?: string;
  metadata?: Record<string, any>;
  return_url?: string;
}

export interface CreateOrderRequest {
  amount: number;
  currency: string;
  description?: string;
  customer_info?: {
    name: string;
    email: string;
    phone?: string;
  };
  shipping_address?: {
    street: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
  items: {
    product_name: string;
    quantity: number;
    unit_price: number;
  }[];
  metadata?: Record<string, any>;
}

export interface CreateRefundRequest {
  payment_id: string;
  amount?: number;
  reason?: string;
  metadata?: Record<string, any>;
}

export interface PaymentFilters {
  status?: PaymentStatus[];
  date_from?: string;
  date_to?: string;
  amount_min?: number;
  amount_max?: number;
  currency?: string;
  payment_method?: string;
  search?: string;
}

export interface OrderFilters {
  status?: OrderStatus[];
  date_from?: string;
  date_to?: string;
  amount_min?: number;
  amount_max?: number;
  currency?: string;
  search?: string;
}

export class PaymentService {
  // 支付意图管理
  static async createPaymentIntent(data: CreatePaymentIntentRequest): Promise<PaymentIntent> {
    const response = await ApiService.post<PaymentIntent>('/merchant/payments/intents', data);
    return response.data;
  }

  static async getPaymentIntent(id: string): Promise<PaymentIntent> {
    const response = await ApiService.get<PaymentIntent>(`/merchant/payments/intents/${id}`);
    return response.data;
  }

  static async listPaymentIntents(
    page: number = 1,
    limit: number = 20,
    filters?: PaymentFilters
  ): Promise<{
    data: PaymentIntent[];
    pagination: {
      page: number;
      limit: number;
      total: number;
    };
  }> {
    const params = {
      page,
      limit,
      ...filters,
    };
    const response = await ApiService.get('/merchant/payments/intents', params);
    return response.data;
  }

  static async confirmPaymentIntent(id: string, payment_method_id?: string): Promise<PaymentIntent> {
    const response = await ApiService.post<PaymentIntent>(
      `/merchant/payments/intents/${id}/confirm`,
      { payment_method_id }
    );
    return response.data;
  }

  static async cancelPaymentIntent(id: string, reason?: string): Promise<PaymentIntent> {
    const response = await ApiService.post<PaymentIntent>(
      `/merchant/payments/intents/${id}/cancel`,
      { reason }
    );
    return response.data;
  }

  static async capturePaymentIntent(id: string, amount?: number): Promise<PaymentIntent> {
    const response = await ApiService.post<PaymentIntent>(
      `/merchant/payments/intents/${id}/capture`,
      { amount }
    );
    return response.data;
  }

  // 支付方式管理
  static async getPaymentMethods(): Promise<{
    cards: any[];
    crypto: any[];
    other: any[];
  }> {
    const response = await ApiService.get('/merchant/payments/methods');
    return response.data;
  }

  static async createPaymentMethod(data: any): Promise<any> {
    const response = await ApiService.post('/merchant/payments/methods', data);
    return response.data;
  }

  static async deletePaymentMethod(id: string): Promise<void> {
    await ApiService.delete(`/merchant/payments/methods/${id}`);
  }

  // 订单管理
  static async createOrder(data: CreateOrderRequest): Promise<Order> {
    const response = await ApiService.post<Order>('/merchant/payments/orders', data);
    return response.data;
  }

  static async getOrder(id: string): Promise<Order> {
    const response = await ApiService.get<Order>(`/merchant/payments/orders/${id}`);
    return response.data;
  }

  static async listOrders(
    page: number = 1,
    limit: number = 20,
    filters?: OrderFilters
  ): Promise<{
    data: Order[];
    pagination: {
      page: number;
      limit: number;
      total: number;
    };
  }> {
    const params = {
      page,
      limit,
      ...filters,
    };
    const response = await ApiService.get('/merchant/payments/orders', params);
    return response.data;
  }

  static async updateOrder(id: string, data: Partial<Order>): Promise<Order> {
    const response = await ApiService.put<Order>(`/merchant/payments/orders/${id}`, data);
    return response.data;
  }

  static async updateOrderStatus(id: string, status: OrderStatus, notes?: string): Promise<Order> {
    const response = await ApiService.put<Order>(`/merchant/payments/orders/${id}`, {
      status,
      notes,
    });
    return response.data;
  }

  // 退款管理
  static async createRefund(data: CreateRefundRequest): Promise<Refund> {
    const response = await ApiService.post<Refund>('/merchant/payments/refunds', data);
    return response.data;
  }

  static async getRefund(id: string): Promise<Refund> {
    const response = await ApiService.get<Refund>(`/merchant/payments/refunds/${id}`);
    return response.data;
  }

  static async listRefunds(
    page: number = 1,
    limit: number = 20,
    filters?: any
  ): Promise<{
    data: Refund[];
    pagination: {
      page: number;
      limit: number;
      total: number;
    };
  }> {
    const params = {
      page,
      limit,
      ...filters,
    };
    const response = await ApiService.get('/merchant/payments/refunds', params);
    return response.data;
  }

  // 争议管理
  static async listDisputes(
    page: number = 1,
    limit: number = 20,
    filters?: any
  ): Promise<{
    data: Dispute[];
    pagination: {
      page: number;
      limit: number;
      total: number;
    };
  }> {
    const params = {
      page,
      limit,
      ...filters,
    };
    const response = await ApiService.get('/merchant/payments/disputes', params);
    return response.data;
  }

  static async getDispute(id: string): Promise<Dispute> {
    const response = await ApiService.get<Dispute>(`/merchant/payments/disputes/${id}`);
    return response.data;
  }

  static async submitDisputeEvidence(id: string, evidence: any): Promise<void> {
    await ApiService.post(`/merchant/payments/disputes/${id}/evidence`, evidence);
  }

  static async acceptDispute(id: string): Promise<void> {
    await ApiService.post(`/merchant/payments/disputes/${id}/accept`);
  }

  static async challengeDispute(id: string): Promise<void> {
    await ApiService.post(`/merchant/payments/disputes/${id}/challenge`);
  }

  // 支付链接
  static async createPaymentLink(data: {
    amount: number;
    currency: string;
    description: string;
    expires_at?: string;
    metadata?: Record<string, any>;
  }): Promise<{
    id: string;
    url: string;
    expires_at: string;
  }> {
    const response = await ApiService.post('/merchant/payments/links', data);
    return response.data;
  }

  static async listPaymentLinks(
    page: number = 1,
    limit: number = 20
  ): Promise<{
    data: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
    };
  }> {
    const params = { page, limit };
    const response = await ApiService.get('/merchant/payments/links', params);
    return response.data;
  }

  static async deletePaymentLink(id: string): Promise<void> {
    await ApiService.delete(`/merchant/payments/links/${id}`);
  }

  // 报表
  static async getTransactionReport(
    date_from: string,
    date_to: string,
    filters?: any
  ): Promise<{
    summary: any;
    transactions: any[];
    charts: any;
  }> {
    const params = {
      date_from,
      date_to,
      ...filters,
    };
    const response = await ApiService.get('/merchant/payments/reports/transactions', params);
    return response.data;
  }

  static async getSummaryReport(
    date_from: string,
    date_to: string
  ): Promise<{
    revenue: any;
    transactions: any;
    refunds: any;
    disputes: any;
  }> {
    const params = { date_from, date_to };
    const response = await ApiService.get('/merchant/payments/reports/summary', params);
    return response.data;
  }

  static async exportReport(
    type: 'transactions' | 'orders' | 'refunds',
    format: 'csv' | 'excel' | 'pdf',
    filters?: any
  ): Promise<{ job_id: string; status: string }> {
    const response = await ApiService.post('/merchant/payments/reports/export', {
      type,
      format,
      filters,
    });
    return response.data;
  }
}

export default PaymentService; 